/**
 * DV-STAGE-003: Session storage persistence hook
 * Handles debounced persistence of stage tester state to sessionStorage
 */

import { useCallback, useRef } from 'react';
import { StageId, SessionStorageState, SESSION_STORAGE_KEYS } from '@/types/stage-tester';
import { setVersionedItem, getVersionedItem } from '@/lib/storage-migration';

interface UseSessionStorageProps {
  stage: StageId;
  onLog: (entry: { ts: string; level: 'debug' | 'info' | 'warn' | 'error'; text: string; meta?: any }) => void;
}

export function useSessionStorage({ stage, onLog }: UseSessionStorageProps) {
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>('');

  /**
   * Load state from sessionStorage on mount (with migration support)
   */
  const loadState = useCallback((): SessionStorageState | null => {
    try {
      const stored = getVersionedItem('stageTester', stage);

      if (!stored) {
        onLog({
          ts: new Date().toISOString(),
          level: 'debug',
          text: `No stored state found for ${stage}`,
          meta: { stage }
        });
        return null;
      }

      const state = stored as SessionStorageState;

      onLog({
        ts: new Date().toISOString(),
        level: 'info',
        text: `Loaded state for ${stage}`,
        meta: {
          stage,
          version: stored.version,
          source: state.source,
          hasSampleId: !!state.sampleId,
          hasCustomJson: !!state.customJson,
          hasOverrides: !!state.overrides,
          hasLastOutputRef: !!state.lastOutputRef
        }
      });

      return state;
    } catch (error) {
      onLog({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to load state for ${stage}`,
        meta: {
          stage,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      });
      return null;
    }
  }, [stage, onLog]);

  /**
   * Save state to sessionStorage with debouncing
   */
  const saveState = useCallback((state: SessionStorageState) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Create debounced save (100ms as specified)
    debounceTimeoutRef.current = setTimeout(() => {
      try {
        const serialized = JSON.stringify(state);

        // Skip save if state hasn't changed
        if (serialized === lastSavedRef.current) {
          return;
        }

        // Use versioned storage
        setVersionedItem('stageTester', stage, state);
        lastSavedRef.current = serialized;

        onLog({
          ts: new Date().toISOString(),
          level: 'debug',
          text: `Saved state for ${stage}`,
          meta: {
            stage,
            source: state.source,
            size: serialized.length,
            hasSampleId: !!state.sampleId,
            hasCustomJson: !!state.customJson?.length,
            hasOverrides: !!state.overrides,
            hasLastOutputRef: !!state.lastOutputRef
          }
        });
      } catch (error) {
        onLog({
          ts: new Date().toISOString(),
          level: 'error',
          text: `Failed to save state for ${stage}`,
          meta: {
            stage,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        });
      }
    }, 100); // 100ms debounce as specified
  }, [stage, onLog]);

  /**
   * Clear stored state for this stage
   */
  const clearState = useCallback(() => {
    try {
      const key = SESSION_STORAGE_KEYS.stageTester(stage);
      sessionStorage.removeItem(key);
      lastSavedRef.current = '';

      // Clear any pending debounced save
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }

      onLog({
        ts: new Date().toISOString(),
        level: 'info',
        text: `Cleared state for ${stage}`,
        meta: { stage }
      });
    } catch (error) {
      onLog({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to clear state for ${stage}`,
        meta: {
          stage,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      });
    }
  }, [stage, onLog]);

  /**
   * Get storage usage summary for debugging
   */
  const getStorageInfo = useCallback(() => {
    try {
      const key = SESSION_STORAGE_KEYS.stageTester(stage);
      const stored = sessionStorage.getItem(key);

      if (!stored) {
        return { exists: false, size: 0 };
      }

      return {
        exists: true,
        size: stored.length,
        state: JSON.parse(stored) as SessionStorageState
      };
    } catch (error) {
      return {
        exists: false,
        size: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }, [stage]);

  return {
    loadState,
    saveState,
    clearState,
    getStorageInfo
  };
}

/**
 * Utility to get all stage tester storage usage
 */
export function getSessionStorageUsage(): Record<string, { size: number; age?: string; valid: boolean }> {
  const usage: Record<string, any> = {};

  try {
    // Check all possible stage storage keys
    const stages: StageId[] = ['script', 'vision', 'storyboard', 'frames', 'video', 'qc', 'export', 'music'];

    stages.forEach(stage => {
      const key = SESSION_STORAGE_KEYS.stageTester(stage);
      const stored = sessionStorage.getItem(key);

      if (stored) {
        try {
          const state = JSON.parse(stored) as SessionStorageState;
          usage[stage] = {
            size: stored.length,
            valid: true,
            source: state.source,
            hasSampleId: !!state.sampleId,
            hasCustomJson: !!state.customJson?.length,
            hasOverrides: !!state.overrides
          };
        } catch {
          usage[stage] = {
            size: stored.length,
            valid: false
          };
        }
      }
    });

    // Also check session outputs
    const sessionOutputsKey = SESSION_STORAGE_KEYS.sessionOutputs;
    const sessionOutputs = sessionStorage.getItem(sessionOutputsKey);
    if (sessionOutputs) {
      usage['sessionOutputs'] = {
        size: sessionOutputs.length,
        valid: true
      };
    }

  } catch (error) {
    console.warn('Failed to get session storage usage:', error);
  }

  return usage;
}

/**
 * Utility to clear all stage tester session storage
 */
export function clearAllSessionStorage(): void {
  try {
    const stages: StageId[] = ['script', 'vision', 'storyboard', 'frames', 'video', 'qc', 'export', 'music'];

    stages.forEach(stage => {
      const key = SESSION_STORAGE_KEYS.stageTester(stage);
      sessionStorage.removeItem(key);
    });

    // Also clear session outputs
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.sessionOutputs);

    console.log('🧹 Cleared all stage tester session storage');
  } catch (error) {
    console.warn('Failed to clear session storage:', error);
  }
}